/// <reference lib="dom" />
/// <reference lib="dom.iterable" />
import { StepAction } from './stepAction';
import { getLogger } from '../../commons/logger';
import { featureFlagsService } from '../../commons/featureFlagsService';
import { getSessionPlayer } from '../../commons/getSessionPlayerRequire';
import { DOMParser } from 'linkedom';
import type { WebDriver } from '../webdriver';
import type { SeleniumTarget } from './stepAction';
import type { ExcludesFalseyTypeGuard } from '../../utils';
import type { LocateStep } from 'clickim/src/common/models/locateStep';
import type { FrameHandler } from 'clickim/src/locators/dynamicParentUtils';
import type { SessionPlayerInit } from 'clickim/src/background/sessionPlayerInit';
import type { VisibilityInfo } from 'clickim/src/background/visibilityUtils';
import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';
import type { ILocateStepActionUtils } from 'clickim/src/background/stepActions/stepActionFactory';

const logger = getLogger('locate-step-action');
const DEFAULT_VISIBILITY_RESULT = { opacity: 1, clientRects: {} };

function shouldUseNativeVisibilityCheck(locateStep: LocateStep, visibilityUtils: SessionPlayerInit['visibilityUtils'], positionUtils: SessionPlayerInit['positionUtils'], driver: WebDriver) {
    if (locateStep.opacity === 0) {
        return false;
    }
    if (locateStep.isShadowed) {
        return false;
    }
    if (visibilityUtils === undefined || positionUtils === undefined) {
        return true;
    }
    if (featureFlagsService.flags.useClickimVisibilityChecks.isEnabled()) {
        return false;
    }
    if (driver.isSafari()) {
        return featureFlagsService.flags.useSafariWebdriverVisibilityChecks.isEnabled();
    }
    return true;
}

function createUtils(driver: WebDriver): ILocateStepActionUtils<SeleniumTarget> {
    return {
        getFrameIdByTestimFrameId() { /* noop */ },

        async setElementResultDataOnContext(target) {
            const seleniumResponse = await driver.getElement(target.locatedElement);
            if (!seleniumResponse.value) {
                throw new Error('Element not found');
            }
            target.seleniumElement = seleniumResponse.value;
        },

        getElementRectangle(target) {
            return driver.getElementRect(target) as Promise<VisibilityInfo>;
        },

        async getOffsets(frameHandler) {
            return [frameHandler.frameOffset || {}];
        },

        htmlStringToDom(htmlString, url) {
            const parser = new DOMParser();

            // Create a wrapper document, since we cannot assign body directly.
            const document = parser.parseFromString('<html><body></body></html>', 'text/html') as unknown as Document;

            document.body.outerHTML = htmlString;
            return Object.assign(document, { TESTIM_URL: url, parsedUsingLinkedom: true as const });
        },

        async isVisible(target, targetElement, rect, locateStep, frameHandler, allOffsets, dom) {
            const { locatorBuilderUtils, codeSnippets, visibilityUtils, positionUtils } = getSessionPlayer();
            const useNativeVisibilityCheck = shouldUseNativeVisibilityCheck(locateStep, visibilityUtils, positionUtils, driver);
            if (useNativeVisibilityCheck) {
                return driver.isVisible(target.seleniumElement);
            }

            // if the project is set to ignore visibility, we should always return visible
            if (driver.getIgnoreVisibility()) {
                return { visible: true, invisibleReason: 'visibility is ignored' };
            }

            if (!target.seleniumElement) {
                return { visible: false, invisibleReason: 'element not found' };
            }

            // this is here for the side effects.
            try {
                await driver.isVisible(target.seleniumElement);
            } catch {
                /* ignored */
            }
            if (!targetElement || locatorBuilderUtils.isEmptyResult(targetElement)) {
                return { visible: false, invisibleReason: 'element not found' };
            }
            const middlePosition = positionUtils.calculateElementMiddlePoint(rect);
            const points = [middlePosition, positionUtils.calculateClickPoint(locateStep.clickOffset, rect)].filter(Boolean as unknown as ExcludesFalseyTypeGuard);
            const code = codeSnippets.getVisibilityCode.getCompoundVisibilityInfoCode(target.locatedElement, points, false, locateStep);
            let response;
            try {
                response = await driver.execute(`return ${code}`);
            } catch (err) {
                logger.error('failed to execute getVisibilityCode', { err });
                throw err;
            }
            const { value } = response || {};
            const elementVisibilityInfo = value.elementVisibilityInfo || DEFAULT_VISIBILITY_RESULT;
            const [middleElementFromPoint, clickElementFromPoint] = value.elementsFromPointResults || [null, null];
            return visibilityUtils.checkElementVisibility(elementVisibilityInfo, locateStep, clickElementFromPoint, middleElementFromPoint, dom, targetElement, false);
        },

        async scrollToElement(frameHandler: FrameHandler, locatedElement: ILocatedElement) {
            const { codeSnippets: { scrollToElement } } = getSessionPlayer();
            await driver.execute(scrollToElement(locatedElement));
        },
    };
}

export class LocateStepAction extends StepAction<LocateStep> {
    override execute() {
        return this.driver.getHTML(this.step);
    }

    static getUtils(driver: WebDriver) {
        return createUtils(driver);
    }

    static getFrameIdByTestimFrameId() {
        logger.warn('Unplanned access to getFrameIdByTestimFrameId()');
        throw new Error('Use .getUtils() instead');
    }

    static setElementResultDataOnContext() {
        logger.warn('Unplanned access to setElementResultDataOnContext()');
        throw new Error('Use .getUtils() instead');
    }

    static getElementRectangle() {
        logger.warn('Unplanned access to getElementRectangle()');
        throw new Error('Use .getUtils() instead');
    }

    static getOffsets() {
        logger.warn('Unplanned access to getOffsets()');
        throw new Error('Use .getUtils() instead');
    }

    static htmlStringToDom() {
        logger.warn('Unplanned access to htmlStringToDom()');
        throw new Error('Use .getUtils() instead');
    }

    static isVisible() {
        logger.warn('Unplanned access to isVisible()');
        throw new Error('Use .getUtils() instead');
    }
}
